/**
 * FieldUpdater - Updates DOM elements when field data changes
 *
 * This class handles updating DOM elements based on field paths using data-dr-field attributes.
 * It supports dot notation for nested fields and array index notation.
 *
 * Key features:
 * - Field path resolution (dot notation, arrays)
 * - DOM element querying by data-dr-field attribute
 * - Element type detection and value setting
 * - Field element caching for performance
 * - Support for text, image, input, link elements
 * - Component update triggering
 */

import type { FieldUpdater as IFieldUpdater } from '../types/index.js';
import { renderIconIntoSvg } from './icon-paths.js';

export class FieldUpdater implements IFieldUpdater {
  private fieldCache: Map<string, HTMLElement[]> = new Map();
  private componentUpdateCallbacks: Map<HTMLElement, () => void> = new Map();

  /**
   * Update a single field by path
   * @param fieldPath - Dot notation path (e.g., "contact.email", "images.0.src")
   * @param value - New value to set
   */
  updateField(fieldPath: string, value: any): void {
    const elements = this.getFieldElements(fieldPath);

    elements.forEach((element) => {
      this.setFieldValue(element, value);

      // Trigger component update if element is inside a component
      this.triggerComponentUpdate(element);
    });
  }

  /**
   * Update all fields from data object
   * @param data - Complete data object
   */
  updateAllFields(data: Record<string, any>): void {
    // Clear cache to ensure we get fresh elements
    this.fieldCache.clear();

    // Find all elements with data-dr-field attributes
    const fieldElements = document.querySelectorAll('[data-dr-field]');

    fieldElements.forEach((element) => {
      const fieldPath = element.getAttribute('data-dr-field');
      if (!fieldPath) {
        return;
      }

      const value = this.resolveFieldPath(data, fieldPath);
      if (value !== undefined) {
        this.setFieldValue(element as HTMLElement, value);
      }
    });

    // Trigger component updates for all components
    this.triggerAllComponentUpdates();
  }

  /**
   * Get all DOM elements for a field path
   * @param fieldPath - Field path to query
   * @returns Array of matching elements
   */
  getFieldElements(fieldPath: string): HTMLElement[] {
    // Check cache first
    if (this.fieldCache.has(fieldPath)) {
      const cached = this.fieldCache.get(fieldPath)!;
      // Verify cached elements are still in DOM
      const validElements = cached.filter((el) => document.contains(el));
      if (validElements.length === cached.length) {
        return validElements;
      }
    }

    // Query DOM for elements
    const elements = Array.from(document.querySelectorAll(`[data-dr-field="${fieldPath}"]`)) as HTMLElement[];

    // Cache the results
    this.fieldCache.set(fieldPath, elements);

    return elements;
  }

  /**
   * Set value on a specific element based on its type
   * @param element - DOM element to update
   * @param value - Value to set
   */
  setFieldValue(element: HTMLElement, value: any): void {
    const tagName = element.tagName.toLowerCase();

    try {
      switch (tagName) {
        case 'img':
          this.setImageValue(element as HTMLImageElement, value);
          break;

        case 'input':
          this.setInputValue(element as HTMLInputElement, value);
          break;

        case 'textarea':
          this.setTextareaValue(element as HTMLTextAreaElement, value);
          break;

        case 'select':
          this.setSelectValue(element as HTMLSelectElement, value);
          break;

        case 'a':
          this.setLinkValue(element as HTMLAnchorElement, value);
          break;

        case 'video':
          this.setVideoValue(element as HTMLVideoElement, value);
          break;

        case 'audio':
          this.setAudioValue(element as HTMLAudioElement, value);
          break;

        case 'source':
          this.setSourceValue(element as HTMLSourceElement, value);
          break;

        case 'svg':
          renderIconIntoSvg(element as unknown as SVGSVGElement, String(value));
          break;

        default:
          this.setTextValue(element, value);
          break;
      }
    } catch (error) {
      console.warn(`Failed to update field element:`, error, { element, value });
    }
  }

  /**
   * Resolve field path in data object (supports dot notation and arrays)
   * @param data - Data object to traverse
   * @param path - Field path (e.g., "contact.email", "images.0.src")
   * @returns Resolved value or undefined
   */
  private resolveFieldPath(data: any, path: string): any {
    const parts = path.split('.');
    let current = data;

    for (const part of parts) {
      if (current === undefined || current === null) {
        return undefined;
      }

      // Handle array indices
      if (/^\d+$/.test(part)) {
        const index = parseInt(part, 10);
        if (Array.isArray(current) && index >= 0 && index < current.length) {
          current = current[index];
        } else {
          return undefined;
        }
      } else {
        current = current[part];
      }
    }

    return current;
  }

  /**
   * Set value for image elements
   */
  private setImageValue(img: HTMLImageElement, value: any): void {
    if (typeof value === 'string') {
      img.src = value;
    } else if (value && typeof value === 'object') {
      if (value.src) {
        img.src = value.src;
      }
      if (value.alt) {
        img.alt = value.alt;
      }
      if (value.title) {
        img.title = value.title;
      }
    }
  }

  /**
   * Set value for input elements
   */
  private setInputValue(input: HTMLInputElement, value: any): void {
    const type = input.type.toLowerCase();

    switch (type) {
      case 'checkbox':
        input.checked = Boolean(value);
        break;

      case 'radio':
        input.checked = input.value === String(value);
        break;

      case 'file':
        // Cannot programmatically set file input values for security reasons
        break;

      default:
        input.value = String(value ?? '');
        break;
    }
  }

  /**
   * Set value for textarea elements
   */
  private setTextareaValue(textarea: HTMLTextAreaElement, value: any): void {
    textarea.value = String(value ?? '');
  }

  /**
   * Set value for select elements
   */
  private setSelectValue(select: HTMLSelectElement, value: any): void {
    select.value = String(value ?? '');
  }

  /**
   * Set value for link elements
   */
  private setLinkValue(link: HTMLAnchorElement, value: any): void {
    if (typeof value === 'string') {
      link.href = value;
    } else if (value && typeof value === 'object') {
      if (value.href) {
        link.href = value.href;
      }
      if (value.text) {
        link.textContent = value.text;
      }
      if (value.title) {
        link.title = value.title;
      }
      if (value.target) {
        link.target = value.target;
      }
    }
  }

  /**
   * Set value for video elements
   */
  private setVideoValue(video: HTMLVideoElement, value: any): void {
    if (typeof value === 'string') {
      video.src = value;
    } else if (value && typeof value === 'object') {
      if (value.src) {
        video.src = value.src;
      }
      if (value.poster) {
        video.poster = value.poster;
      }
      if (value.autoplay !== undefined) {
        video.autoplay = Boolean(value.autoplay);
      }
      if (value.muted !== undefined) {
        video.muted = Boolean(value.muted);
      }
      if (value.loop !== undefined) {
        video.loop = Boolean(value.loop);
      }
      if (value.controls !== undefined) {
        video.controls = Boolean(value.controls);
      }
    }
  }

  /**
   * Set value for audio elements
   */
  private setAudioValue(audio: HTMLAudioElement, value: any): void {
    if (typeof value === 'string') {
      audio.src = value;
    } else if (value && typeof value === 'object') {
      if (value.src) {
        audio.src = value.src;
      }
      if (value.autoplay !== undefined) {
        audio.autoplay = Boolean(value.autoplay);
      }
      if (value.muted !== undefined) {
        audio.muted = Boolean(value.muted);
      }
      if (value.loop !== undefined) {
        audio.loop = Boolean(value.loop);
      }
      if (value.controls !== undefined) {
        audio.controls = Boolean(value.controls);
      }
    }
  }

  /**
   * Set value for source elements (inside video/audio)
   */
  private setSourceValue(source: HTMLSourceElement, value: any): void {
    if (typeof value === 'string') {
      source.src = value;
    } else if (value && typeof value === 'object') {
      if (value.src) {
        source.src = value.src;
      }
      if (value.type) {
        source.type = value.type;
      }
      if (value.media) {
        source.media = value.media;
      }
    }
  }

  /**
   * Set value for text elements (default case)
   */
  private setTextValue(element: HTMLElement, value: any): void {
    const stringValue = String(value ?? '');

    // Check if the element has contenteditable attribute
    if (element.contentEditable === 'true') {
      // For contenteditable elements, preserve formatting if value contains HTML
      if (stringValue.includes('<') && stringValue.includes('>')) {
        element.innerHTML = this.sanitizeHTML(stringValue);
      } else {
        element.textContent = stringValue;
      }
    } else {
      // For regular elements, always use textContent for security
      element.textContent = stringValue;
    }
  }

  /**
   * Basic HTML sanitization to prevent XSS
   */
  private sanitizeHTML(html: string): string {
    const div = document.createElement('div');
    div.textContent = html;
    return div.innerHTML;
  }

  /**
   * Trigger component update for elements inside components
   */
  private triggerComponentUpdate(element: HTMLElement): void {
    // Find parent component element
    const componentElement = element.closest('[data-dr-component]');
    if (!componentElement) {
      return;
    }

    // Check if we have a registered callback for this component
    const callback = this.componentUpdateCallbacks.get(componentElement as HTMLElement);
    if (callback) {
      try {
        callback();
      } catch (error) {
        console.warn('Component update callback failed:', error);
      }
    }
  }

  /**
   * Trigger updates for all registered components
   */
  private triggerAllComponentUpdates(): void {
    this.componentUpdateCallbacks.forEach((callback, element) => {
      try {
        callback();
      } catch (error) {
        console.warn('Component update callback failed:', error, element);
      }
    });
  }

  /**
   * Register a component update callback
   * @param componentElement - Component root element
   * @param callback - Function to call when component needs update
   */
  registerComponentUpdateCallback(componentElement: HTMLElement, callback: () => void): void {
    this.componentUpdateCallbacks.set(componentElement, callback);
  }

  /**
   * Unregister a component update callback
   * @param componentElement - Component root element
   */
  unregisterComponentUpdateCallback(componentElement: HTMLElement): void {
    this.componentUpdateCallbacks.delete(componentElement);
  }

  /**
   * Clear all caches and callbacks (useful for cleanup)
   */
  clear(): void {
    this.fieldCache.clear();
    this.componentUpdateCallbacks.clear();
  }

  /**
   * Get cache statistics for debugging
   */
  getCacheStats(): { fieldCacheSize: number; componentCallbacksSize: number } {
    return {
      fieldCacheSize: this.fieldCache.size,
      componentCallbacksSize: this.componentUpdateCallbacks.size,
    };
  }

  /**
   * Get field cache for debugging (returns a copy to prevent mutation)
   */
  getFieldCache(): Record<string, HTMLElement[]> {
    const cache: Record<string, HTMLElement[]> = {};
    this.fieldCache.forEach((elements, path) => {
      cache[path] = [...elements];
    });
    return cache;
  }

  /**
   * Public method to resolve field path (for debugging API)
   * @param data - Data object to traverse
   * @param path - Field path (e.g., "contact.email", "images.0.src")
   * @returns Resolved value or undefined
   */
  public resolveFieldPathPublic(data: any, path: string): any {
    return this.resolveFieldPath(data, path);
  }
}
